import type { VsKind, VsPolicy } from './api';

export type VsSeverity = 'ok' | 'orange' | 'red' | 'red-absolute' | 'none';

export const swatchClass: Record<VsSeverity, string> = {
  ok: 'bg-emerald-700/80 text-emerald-50',
  orange: 'bg-amber-700/80 text-amber-50',
  red: 'bg-red-800/80 text-red-50',
  'red-absolute': 'bg-rose-900/90 text-rose-50 ring-1 ring-rose-300',
  none: 'bg-slate-800/70 text-slate-500',
};

/**
 * Aborted-push follows the same rule set as a full push (Cut-comparison
 * against the top-N score). Soft-push does NOT — it's a lighter
 * commitment where individual severity flags would over-report.
 * The visual distinction for each lives on the week box.
 */
function kindUsesPushRule(kind: VsKind): boolean {
  return kind === 'push' || kind === 'aborted_push';
}

export function weekSeverity(
  scoreM: number,
  kind: VsKind,
  twentiethM: number | null,
  policy: VsPolicy,
): VsSeverity {
  if (scoreM < policy.redAbsoluteM) return 'red-absolute';
  if (!kindUsesPushRule(kind)) return 'ok';
  if (twentiethM == null) return 'ok';
  if (scoreM < twentiethM * policy.redFactor) return 'red';
  if (scoreM < twentiethM * policy.orangeFactor) return 'orange';
  return 'ok';
}

/**
 * Absolute floor for a single day, in M. Weekly floor is `policy.redAbsoluteM`
 * (43.2 M by default); the daily floor is one sixth of that (7.2 M) — a day
 * under this counts as red-absolute regardless of week kind.
 */
export const DAILY_RED_ABSOLUTE_M = 7.2;

/**
 * Daily severity. Only the absolute floor applies per day — no cut comparison
 * (which is what week-level severity uses). `scoreTenthsM` is stored in 0.1-M
 * units. `dayTwentiethTenthsM` / `kind` / `policy` are kept in the signature
 * so callers stay consistent even though the cut isn't used here.
 */
export function dailySeverity(
  scoreTenthsM: number,
  _kind: VsKind,
  _dayTwentiethTenthsM: number | null,
  _policy: VsPolicy,
): VsSeverity {
  const scoreM = scoreTenthsM / 10;
  if (scoreM < DAILY_RED_ABSOLUTE_M) return 'red-absolute';
  return 'ok';
}

export const KIND_LABEL: Record<VsKind, string> = {
  save: 'Save',
  soft_push: 'Soft Push',
  push: 'Push',
  aborted_push: 'Aborted Push',
};

/**
 * Border classes used by VsWeekBox to visualize week kind + outcome.
 * - Kind sets the border color; aborted_push uses dashed border.
 * - Outcome adds a ring: green (win) / red (loss) / none.
 */
export function weekBoxClass(
  kind: VsKind,
  outcome: 'win' | 'loss' | null,
): string {
  const border =
    kind === 'push'
      ? 'border-indigo-500/70'
      : kind === 'aborted_push'
        ? 'border-rose-500/70 border-dashed'
        : kind === 'soft_push'
          ? 'border-amber-500/70'
          : 'border-slate-600/70';
  const ring =
    outcome === 'win'
      ? 'ring-2 ring-emerald-500/60'
      : outcome === 'loss'
        ? 'ring-2 ring-red-600/60'
        : '';
  return `border ${border} ${ring}`;
}
